/**
 * ChallengePicker
 * Modal for selecting and linking challenges to an event
 */

import React, { useState, useCallback, useMemo } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useChallenges } from "../hooks/useChallenges";
import { ChallengeCard } from "./ChallengeCard";
import { ChallengeWithStatus, ChallengeType, CHALLENGE_LABELS } from "../types";

interface ChallengePickerProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (challenge: ChallengeWithStatus, revealTargets: string[]) => void;
  /** Event ID to check for existing links */
  eventId?: string;
  /** Filter to only show unlinked challenges */
  unlinkedOnly?: boolean;
}

/**
 * Modal for selecting challenges to link to an event
 */
export const ChallengePicker: React.FC<ChallengePickerProps> = ({
  visible,
  onClose,
  onSelect,
  eventId,
  unlinkedOnly = true,
}) => {
  const colors = useThemeColors();
  const { challenges, isLoading, error } = useChallenges({
    status: "active",
    unlinkedOnly,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<ChallengeType | "all">("all");
  const [selectedChallenge, setSelectedChallenge] = useState<ChallengeWithStatus | null>(null);
  const [revealTargets, setRevealTargets] = useState<string[]>(["location"]);

  /**
   * Filter challenges based on search and type
   */
  const filteredChallenges = useMemo(() => {
    return challenges.filter((challenge) => {
      const matchesSearch =
        searchQuery === "" ||
        challenge.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        challenge.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = selectedType === "all" || challenge.challenge_type === selectedType;

      return matchesSearch && matchesType;
    });
  }, [challenges, searchQuery, selectedType]);

  /**
   * Handle reveal target toggle
   */
  const handleToggleRevealTarget = useCallback((target: string) => {
    setRevealTargets((prev) => {
      if (prev.includes(target)) {
        // Remove if already selected (but keep at least one)
        const newTargets = prev.filter((t) => t !== target);
        return newTargets.length > 0 ? newTargets : [target];
      }
      // Add if not selected
      return [...prev, target];
    });
  }, []);

  /**
   * Handle confirm selection
   */
  const handleConfirm = useCallback(() => {
    if (selectedChallenge) {
      onSelect(selectedChallenge, revealTargets);
      setSelectedChallenge(null);
      setRevealTargets(["location"]);
      onClose();
    }
  }, [selectedChallenge, revealTargets, onSelect, onClose]);

  /**
   * Reset state when modal closes
   */
  const handleClose = useCallback(() => {
    setSearchQuery("");
    setSelectedType("all");
    setSelectedChallenge(null);
    setRevealTargets(["location"]);
    onClose();
  }, [onClose]);

  const typeFilters: Array<{ value: ChallengeType | "all"; label: string }> = [
    { value: "all", label: "Alle" },
    { value: "photo", label: "Foto" },
    { value: "quiz", label: "Quiz" },
    { value: "location_checkin", label: "Locatie" },
    { value: "task", label: "Opdracht" },
    { value: "puzzle", label: "Puzzle" },
  ];

  const revealTargetOptions = [
    { value: "location", label: "Locatie", icon: "location" },
    { value: "title", label: "Titel", icon: "text" },
    { value: "details", label: "Beschrijving", icon: "document-text" },
    { value: "all", label: "Alles", icon: "eye-off" },
  ];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <View className="flex-1" style={{ backgroundColor: colors.background }}>
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: colors.border }}
        >
          <TouchableOpacity onPress={handleClose}>
            <Text style={{ color: colors.primary }}>Annuleren</Text>
          </TouchableOpacity>
          <Text className="font-semibold text-lg" style={{ color: colors.text }}>
            Uitdaging koppelen
          </Text>
          <TouchableOpacity
            onPress={handleConfirm}
            disabled={!selectedChallenge}
          >
            <Text
              className="font-semibold"
              style={{ color: selectedChallenge ? colors.primary : colors.textTertiary }}
            >
              Koppelen
            </Text>
          </TouchableOpacity>
        </View>

        {/* Search */}
        <View className="px-4 py-3">
          <View
            className="flex-row items-center px-4 py-2 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <Ionicons name="search" size={20} color={colors.textTertiary} />
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Zoek uitdaging..."
              placeholderTextColor={colors.textTertiary}
              className="flex-1 ml-2"
              style={{ color: colors.text }}
            />
            {searchQuery !== "" && (
              <TouchableOpacity onPress={() => setSearchQuery("")}>
                <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Type filters */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          className="px-4 pb-3"
          contentContainerStyle={{ gap: 8 }}
        >
          {typeFilters.map((filter) => (
            <TouchableOpacity
              key={filter.value}
              onPress={() => setSelectedType(filter.value)}
              className="px-4 py-2 rounded-full"
              style={{
                backgroundColor:
                  selectedType === filter.value ? colors.primary : colors.surfaceSecondary,
              }}
            >
              <Text
                className="font-medium"
                style={{
                  color: selectedType === filter.value ? "#FFFFFF" : colors.text,
                }}
              >
                {filter.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Challenge list */}
        <ScrollView
          className="flex-1 px-4"
          showsVerticalScrollIndicator={false}
        >
          {isLoading ? (
            <View className="py-12 items-center">
              <ActivityIndicator size="large" color={colors.primary} />
              <Text className="mt-4" style={{ color: colors.textSecondary }}>
                Uitdagingen laden...
              </Text>
            </View>
          ) : error ? (
            <View className="py-12 items-center">
              <Ionicons name="alert-circle" size={48} color={colors.error} />
              <Text className="mt-4 text-center" style={{ color: colors.error }}>
                {error}
              </Text>
            </View>
          ) : filteredChallenges.length === 0 ? (
            <View className="py-12 items-center">
              <Ionicons name="search" size={48} color={colors.textTertiary} />
              <Text className="mt-4 text-center" style={{ color: colors.textSecondary }}>
                Geen uitdagingen gevonden
              </Text>
              <Text className="text-sm text-center mt-1" style={{ color: colors.textTertiary }}>
                {unlinkedOnly
                  ? "Maak eerst een uitdaging aan of bekijk gekoppelde uitdagingen"
                  : "Maak eerst een uitdaging aan"}
              </Text>
            </View>
          ) : (
            <>
              {filteredChallenges.map((challenge) => (
                <TouchableOpacity
                  key={challenge.id}
                  onPress={() => setSelectedChallenge(challenge)}
                  activeOpacity={0.7}
                >
                  <View
                    className="rounded-2xl mb-3"
                    style={{
                      borderWidth: 2,
                      borderColor:
                        selectedChallenge?.id === challenge.id
                          ? colors.primary
                          : "transparent",
                    }}
                  >
                    <ChallengeCard
                      challenge={challenge}
                      showLinkStatus={!unlinkedOnly}
                    />
                  </View>
                </TouchableOpacity>
              ))}
            </>
          )}

          {/* Bottom padding */}
          <View className="h-4" />
        </ScrollView>

        {/* Selected challenge config */}
        {selectedChallenge && (
          <View
            className="px-4 py-4 border-t"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}
          >
            <Text className="font-semibold mb-3" style={{ color: colors.text }}>
              Geselecteerd: {selectedChallenge.title}
            </Text>

            <Text className="text-sm mb-2" style={{ color: colors.textSecondary }}>
              Wat onthult deze uitdaging?
            </Text>

            <View className="flex-row flex-wrap">
              {revealTargetOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  onPress={() => handleToggleRevealTarget(option.value)}
                  className="mr-2 mb-2 px-4 py-2 rounded-full flex-row items-center"
                  style={{
                    backgroundColor: revealTargets.includes(option.value)
                      ? colors.primary
                      : colors.surfaceSecondary,
                  }}
                >
                  <Ionicons
                    name={option.icon as any}
                    size={16}
                    color={
                      revealTargets.includes(option.value) ? "#FFFFFF" : colors.textSecondary
                    }
                  />
                  <Text
                    className="ml-2 text-sm font-medium"
                    style={{
                      color: revealTargets.includes(option.value) ? "#FFFFFF" : colors.text,
                    }}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}
      </View>
    </Modal>
  );
};

export default ChallengePicker;

